import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { format, subDays, startOfDay, startOfYear, endOfDay } from 'date-fns'
import { getDateFnsLocale } from '@/lib/dateFormat'
import {
  Button as AriaButton,
  Popover,
  Dialog,
  DialogTrigger,
  RangeCalendar,
  CalendarGrid,
  CalendarCell,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  Heading,
  Label,
  Text,
} from 'react-aria-components'
import { CalendarDate, type DateValue } from '@internationalized/date'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
} from '@untitledui-icons/react/line'
import { cx, sortCx } from '@/lib/utils/cx'
import { Button } from '@/components/base/buttons'

export const styles = sortCx({
  common: {
    root: 'flex w-full flex-col gap-1.5',
    label: 'text-sm font-medium text-fg-secondary',
    labelRequired: 'ml-0.5 text-fg-error-secondary',
    trigger: [
      'inline-flex h-10 w-full items-center justify-start gap-2 rounded-lg border border-gray-300 bg-white px-3.5 text-left text-md shadow-xs transition-colors',
      'dark:bg-gray-900 dark:border-gray-700',
      'hover:border-brand-500 hover:bg-gray-50 dark:hover:bg-gray-700',
      'focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/10',
    ].join(' '),
    triggerPlaceholder: 'text-gray-400',
    triggerIcon: 'size-5 shrink-0 text-gray-400 dark:text-gray-500',
    triggerText: 'truncate text-gray-900 dark:text-white',
    popover: [
      'overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg',
      'dark:bg-gray-900 dark:border-gray-700',
      'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
      'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
    ].join(' '),
    dialog: 'outline-none',
    popoverContent: 'flex',
    presetsSidebar: 'flex flex-col border-r border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900',
    presetsLabel: 'mb-2 px-2 text-xs font-medium text-fg-tertiary',
    presetButton: 'justify-start text-left',
    calendarContainer: 'bg-white p-4 dark:bg-gray-900',
    calendar: 'w-fit',
    calendarHeader: 'mb-4 flex items-center justify-between',
    navButton: 'rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800',
    navIcon: 'size-5 text-fg-tertiary',
    calendarHeading: 'text-sm font-semibold text-gray-900 dark:text-white',
    monthsContainer: 'flex gap-6',
    weekdayHeader: 'size-10 text-center text-sm font-medium text-fg-tertiary',
    calendarCell: [
      'flex size-10 cursor-pointer items-center justify-center text-sm outline-none transition-colors',
      'hover:bg-gray-100 dark:hover:bg-gray-800',
      'focus-visible:ring-2 focus-visible:ring-brand-500',
      // Range start
      'selection-start:rounded-l-full selection-start:bg-brand-600 selection-start:text-white',
      // Range end
      'selection-end:rounded-r-full selection-end:bg-brand-600 selection-end:text-white',
      // Middle of range
      'selected:bg-brand-50 selected:text-brand-700',
      'selected:dark:bg-brand-900/30 selected:dark:text-brand-200',
      // Today
      'today:font-semibold',
      // Outside visible range
      'outside-visible-range:text-fg-disabled outside-visible-range:hover:bg-transparent',
      // Disabled
      'disabled:cursor-not-allowed disabled:text-fg-disabled disabled:hover:bg-transparent',
    ].join(' '),
    description: 'text-sm text-fg-tertiary',
    error: 'text-sm text-fg-error-primary',
  },
})

export interface DateRangeValue {
  start?: Date
  end?: Date
  shortcutLabel?: string
}

export interface DateRangePreset {
  label: string
  id: string
  getValue: () => [Date, Date]
}

export interface DateRangePickerProps {
  /** Current value */
  value?: DateRangeValue
  /** Change handler */
  onChange: (value: DateRangeValue) => void
  /** Label text above the input */
  label?: string
  /** Helper text below the input */
  description?: string
  /** Error message to display */
  errorMessage?: string
  /** Placeholder text */
  placeholder?: string
  /** Custom class names */
  className?: string
  /** Whether the picker is required */
  isRequired?: boolean
  /** Whether the picker is disabled */
  isDisabled?: boolean
  /** Maximum date that can be selected */
  maxDate?: Date
  /** Minimum date that can be selected */
  minDate?: Date
  /** Whether to show preset shortcuts */
  showPresets?: boolean
  /** Custom presets (uses default if not provided) */
  presets?: DateRangePreset[]
}

const defaultPresets: DateRangePreset[] = [
  {
    label: 'last_7_days',
    id: 'last_7_days',
    getValue: () => [startOfDay(subDays(new Date(), 8)), endOfDay(subDays(new Date(), 1))],
  },
  {
    label: 'last_30_days',
    id: 'last_30_days',
    getValue: () => [startOfDay(subDays(new Date(), 31)), endOfDay(subDays(new Date(), 1))],
  },
  {
    label: 'last_90_days',
    id: 'last_90_days',
    getValue: () => [startOfDay(subDays(new Date(), 91)), endOfDay(subDays(new Date(), 1))],
  },
  {
    label: 'this_year',
    id: 'this_year',
    getValue: () => [startOfYear(new Date()), endOfDay(subDays(new Date(), 1))],
  },
  {
    label: 'last_365_days',
    id: 'last_365_days',
    getValue: () => [startOfDay(subDays(new Date(), 366)), endOfDay(subDays(new Date(), 1))],
  },
]

// Helper to convert JS Date to React Aria CalendarDate
function toCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

export function DateRangePicker({
  value,
  onChange,
  label,
  description,
  errorMessage,
  placeholder,
  className,
  isRequired,
  isDisabled,
  maxDate,
  showPresets = true,
  presets = defaultPresets,
}: DateRangePickerProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  // Convert the value to React Aria's format for controlled state
  const calendarValue = useMemo(() => {
    if (value?.start && value?.end) {
      return {
        start: toCalendarDate(value.start),
        end: toCalendarDate(value.end),
      }
    }
    return null
  }, [value?.start, value?.end])

  const handleSelectPreset = (preset: DateRangePreset) => {
    const [start, end] = preset.getValue()
    onChange({
      start,
      end,
      shortcutLabel: preset.id,
    })
    setIsOpen(false)
  }

  const getDisplayLabel = (): string => {
    if (value?.shortcutLabel) {
      const preset = presets.find((p) => p.id === value.shortcutLabel)
      if (preset) {
        return t(`resources.generic.date_shortcuts.${preset.label}`, {
          defaultValue: preset.label.replace(/_/g, ' '),
        })
      }
    }

    if (value?.start) {
      const locale = getDateFnsLocale()
      const startStr = format(value.start, 'PP', { locale })
      const endStr = value.end ? format(value.end, 'PP', { locale }) : startStr
      return `${startStr} – ${endStr}`
    }

    return placeholder || t('components.date_picker.select_date_range')
  }

  const hasValue = Boolean(value?.start)

  return (
    <div className={cx(styles.common.root, className)}>
      {label && (
        <Label className={styles.common.label}>
          {label}
          {isRequired && <span className={styles.common.labelRequired}>*</span>}
        </Label>
      )}

      <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <AriaButton
          isDisabled={isDisabled}
          className={cx(
            styles.common.trigger,
            !hasValue && styles.common.triggerPlaceholder
          )}
        >
          <CalendarIcon className={styles.common.triggerIcon} />
          <span className={hasValue ? styles.common.triggerText : undefined}>
            {getDisplayLabel()}
          </span>
        </AriaButton>
        <Popover placement="bottom start" className={styles.common.popover}>
          <Dialog className={styles.common.dialog}>
            <div className={styles.common.popoverContent}>
              {/* Presets sidebar */}
              {showPresets && (
                <div className={styles.common.presetsSidebar}>
                  <div className={styles.common.presetsLabel}>
                    {t('components.date_picker.quick_select')}
                  </div>
                  {presets.map((preset) => (
                    <Button
                      key={preset.id}
                      color={value?.shortcutLabel === preset.id ? 'secondary' : 'tertiary'}
                      size="sm"
                      className={styles.common.presetButton}
                      onClick={() => handleSelectPreset(preset)}
                    >
                      {t(`resources.generic.date_shortcuts.${preset.label}`, {
                        defaultValue: preset.label.replace(/_/g, ' '),
                      })}
                    </Button>
                  ))}
                </div>
              )}

              {/* Calendar */}
              <div className={styles.common.calendarContainer}>
                <RangeCalendar
                  className={styles.common.calendar}
                  visibleDuration={{ months: 2 }}
                  aria-label={t('components.date_picker.select_date_range')}
                  value={calendarValue}
                  isDateUnavailable={(date: DateValue) => {
                    if (maxDate) {
                      const jsDate = new Date(date.year, date.month - 1, date.day)
                      return jsDate > maxDate
                    }
                    return false
                  }}
                  onChange={(range) => {
                    if (range) {
                      const start = startOfDay(new Date(
                        range.start.year,
                        range.start.month - 1,
                        range.start.day
                      ))
                      const end = endOfDay(new Date(
                        range.end.year,
                        range.end.month - 1,
                        range.end.day
                      ))
                      onChange({
                        start,
                        end,
                        shortcutLabel: undefined,
                      })
                    }
                  }}
                >
                  <header className={styles.common.calendarHeader}>
                    <AriaButton slot="previous" className={styles.common.navButton}>
                      <ChevronLeftIcon className={styles.common.navIcon} />
                    </AriaButton>
                    <Heading className={styles.common.calendarHeading} />
                    <AriaButton slot="next" className={styles.common.navButton}>
                      <ChevronRightIcon className={styles.common.navIcon} />
                    </AriaButton>
                  </header>
                  <div className={styles.common.monthsContainer}>
                    <CalendarGrid>
                      <CalendarGridHeader>
                        {(day) => (
                          <CalendarHeaderCell className={styles.common.weekdayHeader}>
                            {day}
                          </CalendarHeaderCell>
                        )}
                      </CalendarGridHeader>
                      <CalendarGridBody>
                        {(date) => (
                          <CalendarCell
                            date={date}
                            className={styles.common.calendarCell}
                          />
                        )}
                      </CalendarGridBody>
                    </CalendarGrid>
                    <CalendarGrid offset={{ months: 1 }}>
                      <CalendarGridHeader>
                        {(day) => (
                          <CalendarHeaderCell className={styles.common.weekdayHeader}>
                            {day}
                          </CalendarHeaderCell>
                        )}
                      </CalendarGridHeader>
                      <CalendarGridBody>
                        {(date) => (
                          <CalendarCell
                            date={date}
                            className={styles.common.calendarCell}
                          />
                        )}
                      </CalendarGridBody>
                    </CalendarGrid>
                  </div>
                </RangeCalendar>
              </div>
            </div>
          </Dialog>
        </Popover>
      </DialogTrigger>

      {description && !errorMessage && (
        <Text slot="description" className={styles.common.description}>
          {description}
        </Text>
      )}
      {errorMessage && (
        <Text slot="errorMessage" className={styles.common.error}>
          {errorMessage}
        </Text>
      )}
    </div>
  )
}

export default DateRangePicker
